import { ContentCopy } from '@mui/icons-material';
import { Button, Paper, Stack } from '@mui/material';
import { t } from 'i18next';
import { groupBy } from 'lodash';
import { BackButton } from '../../../common/components/BackButton';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { RuleCategory } from '../../../common/enums/RuleCategory';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { PATHS } from '../../../routing/routes';
import { CardLoader } from '../components/CardLoader';
import { PresetSubtitle } from '../components/PresetSubtitle';
import { ProfileNameInput } from '../components/ProfileNameInput';
import { SaveButton } from '../components/SaveButton';
import { TracerTable } from '../components/TracerTable/TracerTable';
import { TracerProfile } from '../models/TracerProfile';
import { TracerRule } from '../models/TracerRule';
import { OrganizationProfilesService } from '../services/OrganizationProfilesService';
import { RulesService } from '../services/RulesService';
import { TracerStore } from '../store/TracerStore';

type Props = {
  profile: TracerProfile;
  title: string;
  breadcrumbName: string;
  breadcrumbPath: string;
} & ({ editable: true; canDuplicate?: undefined } | { editable?: undefined; canDuplicate: boolean });

type State = BaseState & {
  loading: boolean;
};

export class TracerController extends BaseController<Props, State> {
  private readonly rulesService = new RulesService();
  private readonly profilesService = new OrganizationProfilesService();

  private tracerStore: TracerStore = TracerStore.getInstance();

  constructor(props: Props, context: unknown) {
    super(props, context);

    const { profile, editable = false } = props;

    this.tracerStore.setStore({
      profile,
      editable,
    });

    this.state = {
      ...super.getBaseState(),
      loading: true,
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.fetchData();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.rulesService.cancelAllRequests();
    this.profilesService.cancelAllRequests();
  }

  render() {
    const loading = this.state.loading || this.tracerStore.getRuleGroups().size === 0 || this.tracerStore.getAvailableTraceTypes().length === 0;
    const { profile, title, editable = false, canDuplicate } = this.props;

    return this.renderBase(
      <SecondaryNav
        title={title}
        subtitle={profile.isPreset() && !editable ? <PresetSubtitle /> : undefined}
        rightSection={
          editable ? (
            <SaveButton store={this.tracerStore} onClick={this.onSaveProfile} />
          ) : canDuplicate ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ContentCopy />}
              href={UrlHelper.generatePath(PATHS.PROFILES_CREATE_TEMPLATE, { profileId: profile.getId().toString() })}
            >
              {t('duplicateProfile')}
            </Button>
          ) : undefined
        }
        getBreadcrumbs={this.getBreadcrumbs}
        loading={loading}
      >
        <Stack gap={2}>
          <BackButton />
          {loading ? (
            <CardLoader />
          ) : (
            <>
              {editable && (
                <Paper elevation={2} sx={{ p: 2 }}>
                  <ProfileNameInput store={this.tracerStore} />
                </Paper>
              )}
              <TracerTable />
            </>
          )}
        </Stack>
      </SecondaryNav>
    );
  }

  private async fetchData() {
    try {
      const rules = await this.rulesService.getTracerRules();

      const sorted = rules.sort((r1, r2) => r1.getNumber() - r2.getNumber());
      const groups = groupBy(sorted, (r) => r.getCategory());
      const ruleGroups = new Map(Object.entries(groups)) as Map<RuleCategory, TracerRule[]>;
      this.tracerStore.setRuleGroups(ruleGroups);

      const availableTraceTypes = await this.rulesService.getAvailableTraceTypes();
      this.tracerStore.setAvailableTraceTypes(availableTraceTypes);

      this.setState({ loading: false });
    } catch (e: unknown) {
      this.setState({ loading: false });
      this.handleError(e, { invalidUrl: t('nonExistingProfile') });
    }
  }

  @bind
  private async onSaveProfile() {
    const name = this.tracerStore.getEditableProfileName();
    const expiringSoon = this.tracerStore.getExpiringSoon();
    const disabledRules = Array.from(this.tracerStore.getDisabledRules());
    const rsaProfile = this.tracerStore.getRsaCards().getValuesInApiFormat();
    const symmetricProfile = this.tracerStore.getSymmetricCards().getValuesInApiFormat();
    const ecProfile = this.tracerStore.getEcCards().getValuesInApiFormat();
    const certificateValidityProfile = this.tracerStore.getCertificateValidityCards().getValuesInApiFormat();
    const staleKeyProfile = this.tracerStore.getStaleKeyCards().getValuesInApiFormat();
    try {
      const profile = {
        name,
        disabledRules,
        expiringSoon,
        symmetricProfile,
        rsaProfile,
        ecProfile,
        certificateValidityProfile,
        staleKeyProfile,
      };
      const profileId = await this.profilesService.createTracerProfile(profile);
      this.redirect(UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: profileId.toString() }));
    } catch (e: unknown) {
      this.handleError(e);
    }
  }

  @bind
  private getBreadcrumbs() {
    const { breadcrumbName, breadcrumbPath } = this.props;
    return this.breadcrumbFactory.getProfileBreadcrumb({ label: breadcrumbName, path: breadcrumbPath });
  }
}
